import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { confirm } from 'devextreme/ui/dialog';
import { iRootDispatch, iRootState } from '../../../store/store';
import styles from './OrderEditForm.module.scss';
import OrderForward, { OrderForwardData, OrderForwardDataKeys } from './order-forward/OrderForward';
import OrderReturn, { OrderReturnData, OrderReturnDataKeys } from './order-return/OrderReturn';
import OrderSummary, { OrderSummaryData, OrderSummaryDataKeys } from './order-summary/OrderSummary';
import Stepper from '@material-ui/core/Stepper/Stepper';
import Step from '@material-ui/core/Step/Step';
import StepLabel from '@material-ui/core/StepLabel/StepLabel';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import {
    OrderEditFormBackButtonId,
    OrderEditFormNextButtonId,
    OrderEditFormDeleteButtonId,
    OrderEditFormBackToListButtonId,
} from './inspectorOptions';
import OrderTimer, { OrderTimerData } from './order-timer/OrderTimer';
import enOrderState from '../../../api/enums/enOrderState';
import {
    COSOrdersStore,
    releaseLockPayload,
    renewLockPayload,
    singleByIdWithKeysPayload,
    validateOrderPayload,
} from '../../../store/models/COSOrdersModel';
import { PATH_ORDERS } from '../../../constants/routerPaths';
import toast from 'fulogics-ui-web/dist/utils/toast';
import useInterval from 'fulogics-ui-core/dist/layout/hooks/useInterval';
import { useIsWindowFocused } from 'fulogics-ui-web/dist/layout/hooks/useIsWindowFocused';
import OrderLockTimerPopup from './order-lock-timer-popup/OrderLockTimerPopup';
import OrderLockPopup, {
    OrderLockPopupData,
    OrderLockPopupDataKeys,
} from './order-lock-popup/OrderLockPopup';
import { getUniqueRules, OrderValidationResult } from '../order-error-info/OrderErrorInfo';
import { ClickEvent } from 'devextreme/ui/button';
import { useVStore } from 'fulogics-ui-web/dist/layout/hooks/useVStore';
import InspectorVariablesContext from 'fulogics-ui-web/dist/layout/inspector/context/InspectorVariablesContext';
import enOrderError from '../../../api/enums/enOrderError';

const mapStateToProps = (state: iRootState) =>
    ({
        currentUserId: state.Identity?.userProfile?.id,
    } as OrderEditFormDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderEditFormDisplayOptions) =>
    ({
        releaseLock: _dispatch.COSOrders.releaseLock,
        renewLock: _dispatch.COSOrders.renewLock,
        getOrderById: _dispatch.COSOrders.singleByIdWithKeys,
        validate: _dispatch.COSOrders.validate,
        ..._ownDispatch,
    } as OrderEditFormDisplayOptions);

const OrderEditForm = connect(
    mapStateToProps,
    mapDispatch as any
)((props: OrderEditFormProps) => {
    const { t } = useTranslation(['OrderEditForm', 'ErrorType']);
    const { push, listen } = useHistory();

    const [activeStep, setActiveStep] = useState<number>(0);
    const [validationResult, setValidationResult] = useState<OrderValidationResult | undefined>(
        props.data?.validationResult
    );
    const [editingDeadline, setEditingDeadline] = useState<Date | undefined>(
        props?.data?.currentEditing?.editingDeadline
    );

    const canEdit =
        // @ts-ignore
        !props.data?.currentEditing?.editingBy?.id?._value ||
        // @ts-ignore
        props.data?.currentEditing?.editingBy?.id?._value === props?.currentUserId;

    const steps = [t('forwardStepTitle'), t('returnStepTitle'), t('summaryStepTitle')];
    const { onDone } = props;
    const onNextClick = useCallback(
        (e: ClickEvent) => {
            if (activeStep === 2) {
                validateOrder().then((result) => {
                    if (result?.isValid) {
                        onDone?.();
                    } else {
                        const errors = getUniqueRules(result);

                        toast.error(
                            errors.map((type) => t(`ErrorType:${type}`)).join('. '),
                            t('validationErrorToastTitle')
                        );
                    }
                });
            } else {
                setActiveStep((step) => step + 1);
            }
        },
        [activeStep, onDone]
    );
    const onBackClick = useCallback((e: ClickEvent) => {
        setActiveStep((step) => step - 1);
    }, []);

    const isDraft = props?.data?.state === enOrderState.Draft;

    const handleDelete = () => {
        if (isDraft) {
            confirm(`<i>${t('deleteModal.content')}</i>`, `${t('deleteModal.title')}`).then(
                (result) => {
                    if (result) {
                        COSOrdersStore.remove(props?.data?.id).then(() => {
                            push(PATH_ORDERS);
                        });
                    }
                }
            );
        }
    };

    const releaseLock = useCallback(
        () =>
            props?.releaseLock?.({ id: props?.data?.id ?? '-1' }).catch((er) => {
                toast.exception(er);
            }),
        [props.releaseLock]
    );

    const handleBackToList = useCallback(() => {
        props?.releaseLock?.({ id: props?.data?.id ?? '-1' })?.then(() => {
            push(PATH_ORDERS);
        });
    }, [props.releaseLock, push]);

    const renewLock = useCallback(
        () =>
            props
                .renewLock?.({
                    id: props?.data?.id ?? '-1',
                })
                .then(async () => {
                    await props
                        ?.getOrderById?.({
                            id: props?.data?.id ?? '-1',
                            keys: ['currentEditing.editingDeadline'],
                        })
                        ?.then((data: any) => {
                            setEditingDeadline(data?.currentEditing?.editingDeadline);
                        });
                })
                .catch((er) => {
                    toast.exception(er);
                }),
        [props.renewLock]
    );

    const backToList = () => push(PATH_ORDERS);

    useEffect(() => {
        return listen((_, action) => {
            if (action === 'POP' && canEdit) {
                props?.releaseLock?.({ id: props?.data?.id ?? '-1' });
            }
        });
    }, [canEdit]);

    useEffect(
        () => () => {
            if (canEdit) {
                props?.releaseLock?.({ id: props?.data?.id ?? '-1' });
            }
        },
        [canEdit]
    );

    const isWindowFocused = useIsWindowFocused();

    useInterval(
        () => {
            if (isWindowFocused && canEdit) {
                renewLock?.();
            }
        },
        1000 * 60 * 2 // co 2 min
    );

    const validateOrder = useCallback(async () => {
        const validationResult = await props?.validate?.({
            id: props?.data?.id as string,
        });

        setValidationResult(validationResult);

        return validationResult;
    }, [props?.validate]);

    const vStore = useVStore(
        () => ({
            variables: {
                validationResult,
            },
        }),
        [validationResult]
    );

    var isOverload =
        validationResult &&
        !!validationResult?.brokenRules?.some(
            (result) =>
                result?.type === enOrderError.ReturnRouteCarrierCountOverload ||
                result?.type === enOrderError.ForwardRouteCarrierCountOverload
        );

    return (
        <div className={classNames(styles.container, props.className)}>
            <InspectorVariablesContext store={vStore}>
                {canEdit && (
                    <OrderLockTimerPopup
                        onBack={handleBackToList}
                        onLockExpire={releaseLock}
                        onContinue={renewLock}
                        deadline={editingDeadline}
                    />
                )}
                <OrderLockPopup visible={!canEdit} onBack={backToList} data={props.data} />
                <div className={styles.formStepDiv}>
                    <div className={styles.roundStepperDiv}>
                        <Stepper activeStep={activeStep} className={styles.stepper}>
                            {steps.map((label, index) => {
                                return (
                                    <Step key={`${label}-${index}`}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                        <div className={styles.orderTimerEmptySpace} />
                        <div className={styles.orderTimerDiv}>
                            {props.data?.draftCloseDeadline && <OrderTimer data={props.data} />}
                        </div>
                    </div>
                    {activeStep === 0 && (
                        <OrderForward
                            containerClassName={styles.fullHeight}
                            data={props.data}
                            validateOrder={validateOrder}
                        />
                    )}
                    {activeStep === 1 && (
                        <OrderReturn
                            containerClassName={styles.fullHeight}
                            data={props.data}
                            validateOrder={validateOrder}
                        />
                    )}
                    {activeStep === 2 && <OrderSummary data={props.data} />}
                </div>
                <div className={styles.buttonsDiv}>
                    <div className={classNames(styles.backButton)}>
                        {activeStep !== 0 && (
                            <Button inspectorId={OrderEditFormBackButtonId} onClick={onBackClick}>
                                {t('backButton')}
                            </Button>
                        )}
                    </div>
                    <div className={classNames(styles.nextButton)}>
                        <Button
                            inspectorId={OrderEditFormBackToListButtonId}
                            onClick={handleBackToList}
                        />
                        {isDraft && (
                            <Button
                                className={classNames(styles.cancelButton)}
                                inspectorId={OrderEditFormDeleteButtonId}
                                onClick={handleDelete}
                            />
                        )}
                        <Button
                            inspectorId={OrderEditFormNextButtonId}
                            disabled={isOverload}
                            text={
                                activeStep === 2
                                    ? props.doneButtonLabel
                                    : t('OrderEditForm:nextButton')
                            }
                            onClick={onNextClick}
                        />
                    </div>
                </div>
            </InspectorVariablesContext>
        </div>
    );
});

export default OrderEditForm;

export type OrderEditFormProps = {
    data?: OrderEditFormData;
} & OrderEditFormDisplayOptions;

export class OrderEditFormDisplayOptions {
    className?: string;
    doneButtonLabel?: string;
    currentUserId?: string;
    //
    onDone?: () => Promise<void>;
    onChangeOrderDate?: () => Promise<void>;
    getOrderById?: <T>(payload: singleByIdWithKeysPayload) => Promise<T>;
    //
    releaseLock?: (payload: releaseLockPayload) => Promise<void>;
    renewLock?: (payload: renewLockPayload) => Promise<void>;
    validate?: (payload?: validateOrderPayload) => Promise<OrderValidationResult>;
}

export type OrderEditFormData = {
    id?: string;
    state?: enOrderState;
    currentEditing?: {
        editingDeadline?: Date;
    };
    validationResult?: OrderValidationResult;
} & OrderTimerData &
    OrderForwardData &
    OrderReturnData &
    OrderSummaryData &
    OrderLockPopupData;

export const OrderEditFormDataKeys = [
    'id',
    'currentEditing.editingDeadline',
    ...OrderLockPopupDataKeys,
    ...OrderForwardDataKeys,
    ...OrderReturnDataKeys,
    ...OrderSummaryDataKeys,
];
